import React from 'react';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import { Lock, Crown, TrendingUp, CheckCircle } from 'lucide-react';

interface HabitLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  currentCount: number;
  maxAllowed: number;
  subscriptionTier: string;
}

export const HabitLimitModal: React.FC<HabitLimitModalProps> = ({
  isOpen,
  onClose,
  onUpgrade,
  currentCount,
  maxAllowed,
  subscriptionTier
}) => {
  const progressPercentage = Math.min((currentCount / maxAllowed) * 100, 100);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="text-center p-6">
        {/* Header */}
        <div className="flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30 rounded-full mx-auto mb-6">
          <Lock className="w-10 h-10 text-orange-600 dark:text-orange-400" />
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Habit Limit Reached
        </h2>
        
        <p className="text-gray-600 dark:text-gray-400 text-lg mb-6 max-w-md mx-auto">
          You've reached your limit of <span className="font-semibold">{maxAllowed} habits</span> on the {subscriptionTier} plan. 
          Upgrade to create unlimited habits and track more of your goals!
        </p>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
            <span>Current Habits</span>
            <span>{currentCount}/{maxAllowed}</span>
          </div>
          <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Upgrade Benefits */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Upgrade Benefits
          </h3>
          <div className="space-y-2 text-left">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="text-gray-700 dark:text-gray-300">Unlimited habits</span>
            </div>
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-blue-500 flex-shrink-0" />
              <span className="text-gray-700 dark:text-gray-300">Advanced analytics</span>
            </div>
            <div className="flex items-center gap-3">
              <Crown className="w-5 h-5 text-purple-500 flex-shrink-0" />
              <span className="text-gray-700 dark:text-gray-300">Priority support</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={onUpgrade}
            variant="primary"
            className="flex-1 sm:flex-none px-8"
          >
            <Crown className="w-4 h-4 mr-2" />
            Upgrade Now
          </Button>
          
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1 sm:flex-none px-8"
          >
            Maybe Later
          </Button>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
          Start with a 7-day free trial • Cancel anytime
        </p>
      </div>
    </Modal>
  );
};
